import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H2 from 'UIComponents/elements/headings/H2';
import { isFulfilled, isUninitialized } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import { SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
// @ts-expect-error untyped dependency
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import ReviewsListEmptyState from './ReviewsListEmptyState';
import ReviewWizard from './ReviewWizard';
import { getIsPublicApp } from '../../selectors/app';
import { getReviewFilters, getReviewsList, getReviewsListRequestState, getReviewStats, getReviewStatsRequestState, getReviewSubmitted, getReviewsTotal
// @ts-expect-error untyped dependency
} from '../../selectors/reviews';
import { fetchReviews, fetchReviewStats, initializeReviewFilters, resetReviewsListStatus, resetReviewStatsStatus, updateReviewFilters
// @ts-expect-error untyped dependency
} from '../../actions/ReviewsActions';
import AssetReviewRenderer from './AssetReviewRenderer';
import WriteReviewButton from './WriteReviewButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const REVIEWS_LIST_WIDTH = 750;
export default function Reviews({
  name,
  exampleUrl,
  isPreview,
  offeringId,
  offeringPortalId,
  offeringType,
  onTrackInteraction,
  providerName,
  onReviewsFulfilled,
  reviewType,
  supportUrl,
  thumbnailUrl,
  versionNumber
}) {
  const {
    pathname
  } = useLocation();
  const isReviewLink = pathname.includes('/write-review');
  const dispatch = useDispatch();
  const [wizardOpen, setWizardOpen] = useState(isReviewLink);
  const [searchTerm, setSearchTerm] = useState('');
  const isPublicApp = useSelector(getIsPublicApp);
  const reviewSubmitted = useSelector(getReviewSubmitted);
  const reviewsListRequestState = useSelector(getReviewsListRequestState);
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    entityId: offeringId,
    entityPortalId: offeringPortalId,
    isPublicApp,
    reviewPending: reviewSubmitted,
    reviewType
  });
  const reviewsList = useSelector(getReviewsList);
  const reviewStatsRequestState = useSelector(getReviewStatsRequestState);
  const reviewStats = useSelector(getReviewStats);
  const reviewsTotal = useSelector(getReviewsTotal); // Based on current filters
  const reviewsCount = reviewStats.get('count'); // All reviews
  const reviewFilters = useSelector(getReviewFilters);
  const {
    maxWidth
  } = useResponsiveProps({
    maxWidth: {
      lg: REVIEWS_LIST_WIDTH
    }
  });
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      section: 'review'
    }));
  }, [onTrackInteraction]);
  useEffect(() => {
    dispatch(initializeReviewFilters(offeringId));
  }, [dispatch, offeringId]);
  useEffect(() => {
    if (isUninitialized(reviewsListRequestState) && reviewFilters.entityId === offeringId) {
      dispatch(fetchReviews(reviewFilters.toSearchQuery(), [reviewType]));
    }
  }, [dispatch, offeringId, reviewFilters, reviewsListRequestState, reviewType]);
  useEffect(() => {
    if (isUninitialized(reviewStatsRequestState)) {
      dispatch(fetchReviewStats(offeringId, reviewType));
    }
  }, [dispatch, reviewStatsRequestState, offeringId, reviewType]);
  useEffect(() => {
    return () => {
      dispatch(resetReviewsListStatus());
      dispatch(resetReviewStatsStatus());
    };
  }, [dispatch]);
  useEffect(() => {
    if (isFulfilled(reviewStatsRequestState) && isFulfilled(reviewsListRequestState)) {
      onReviewsFulfilled(true);
    }
  }, [reviewStatsRequestState, reviewsListRequestState, onReviewsFulfilled]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    if (textKey === SEARCH_KEY) {
      setSearchTerm(value);
    }
    dispatch(updateReviewFilters({
      [textKey.toLowerCase()]: value
    }, [reviewType]));
  }, [dispatch, reviewType, setSearchTerm]);
  const renderedWriteReviewButton = useMemo(() => /*#__PURE__*/_jsx(UIFlex, {
    justify: "end",
    children: /*#__PURE__*/_jsx(WriteReviewButton, {
      className: "m-y-4",
      disabled: !!isPreview,
      onClick: () => setWizardOpen(true),
      unreviewableReason: unreviewableReason
    })
  }), [isPreview, unreviewableReason, setWizardOpen]);
  const renderedReviewsList = useMemo(() => /*#__PURE__*/_jsxs("div", {
    style: {
      maxWidth
    },
    children: [/*#__PURE__*/_jsx(ReviewsHeader, {
      onUpdateFilters: onUpdateFilters,
      renderedButtonRow: renderedWriteReviewButton,
      reviewsCount: reviewStats.get('count'),
      reviewFilters: reviewFilters
    }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
      onUpdateFilters: onUpdateFilters,
      reviewFilters: reviewFilters,
      reviewStats: reviewStats
    }), /*#__PURE__*/_jsx(ReviewsPaginatedList, {
      onUpdateFilters: onUpdateFilters,
      ReviewRenderer: AssetReviewRenderer,
      reviews: reviewsList,
      reviewsFetchStatus: reviewsListRequestState,
      reviewFilters: reviewFilters,
      reviewsTotal: reviewsTotal,
      searchTerm: searchTerm,
      reviewType: reviewType
    })]
  }), [onUpdateFilters, maxWidth, renderedWriteReviewButton, reviewFilters, reviewsList, reviewStats, reviewsTotal, reviewsListRequestState, searchTerm, reviewType]);
  const renderedReviewsBody = useMemo(() => {
    if (reviewsCount > 2) {
      return /*#__PURE__*/_jsxs(UIGrid, {
        className: "m-top-8",
        children: [/*#__PURE__*/_jsx(UIGridItem, {
          size: {
            xs: 12,
            lg: 4
          },
          children: /*#__PURE__*/_jsx(LoadingWrapper, {
            requestStatus: reviewStatsRequestState,
            children: /*#__PURE__*/_jsx(ReviewsListStats, {
              onUpdateFilters: onUpdateFilters,
              reviewStats: reviewStats,
              reviewType: reviewType
            })
          })
        }), /*#__PURE__*/_jsx(UIGridItem, {
          size: {
            xs: 12,
            lg: 8
          },
          children: renderedReviewsList
        })]
      });
    } else if (reviewsCount && reviewsCount <= 2) {
      return renderedReviewsList;
    } else if (!reviewsCount) {
      return /*#__PURE__*/_jsx(ReviewsListEmptyState, {
        isPreview: !!isPreview,
        onWriteReviewClick: () => setWizardOpen(true),
        unreviewableReason: unreviewableReason
      });
    }
    return null;
  }, [isPreview, onUpdateFilters, renderedReviewsList, reviewsCount, reviewStats, reviewStatsRequestState, reviewType, unreviewableReason]);
  const renderedWizard = useMemo(() => {
    return /*#__PURE__*/_jsx(LoadingWrapper, {
      requestStatus: reviewabilityRequestStatus,
      children: /*#__PURE__*/_jsx(ReviewWizard, {
        name: name,
        thumbnailUrl: thumbnailUrl,
        offeringId: offeringId,
        offeringType: offeringType,
        onClose: () => setWizardOpen(false),
        onSuccess: () => setWizardOpen(false),
        onTrackInteraction: trackInteraction,
        providerName: providerName,
        reviewType: reviewType,
        supportLink: supportUrl,
        slug: exampleUrl,
        unreviewableReason: unreviewableReason,
        versionNumber: versionNumber
      })
    });
  }, [exampleUrl, name, offeringId, offeringType, providerName, reviewabilityRequestStatus, supportUrl, thumbnailUrl, trackInteraction, unreviewableReason, versionNumber, reviewType]);
  return /*#__PURE__*/_jsxs("div", {
    style: {
      position: 'relative'
    },
    children: [/*#__PURE__*/_jsx(H2, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.reviews.title",
        options: {
          name
        }
      })
    }), renderedReviewsBody, wizardOpen && renderedWizard]
  });
}